import { useState, useEffect } from 'react';
import { X, Calendar, Star } from 'lucide-react';
import { api } from '@/lib/api';
import type { RamadanEvent } from '@/types';

export default function EventNotification() {
  const [event, setEvent] = useState<RamadanEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await api.getEvents();
        if (response.success && response.data) {
          const today = new Date().toISOString().split('T')[0];
          const todayEvent = response.data.find(e => e.event_date === today);
          if (todayEvent) {
            setEvent(todayEvent);
            setTimeout(() => setVisible(true), 500);
          }
        }
      } catch {
        // silently fail
      }
    };

    fetchEvents();
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => setDismissed(true), 300);
  };

  if (!event || dismissed) return null;

  return (
    <div
      className={`fixed z-50 transition-all duration-300 ease-out
        bottom-4 right-4 left-4 sm:left-auto sm:max-w-sm md:max-w-md
        ${visible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
    >
      <div className="relative overflow-hidden rounded-xl border border-gold-500/30 bg-gray-900/95 backdrop-blur-md shadow-lg shadow-gold-500/10">
        {/* Gold accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-400 via-gold-500 to-gold-600" />

        <div className="p-4 pt-5">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 left-2 p-1 rounded-full text-gray-400 hover:text-cream hover:bg-white/10 transition-colors"
          >
            <X size={16} />
          </button>

          {/* Header */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gold-500/20">
              <Star size={16} className="text-gold-400" />
            </div>
            <span className="text-sm font-bold text-gold-400">
              فعالية اليوم - اليوم {event.ramadan_day} من رمضان
            </span>
          </div>

          {/* Event image */}
          {event.image_url && (
            <div className="mb-3 rounded-lg overflow-hidden border border-gray-700/50">
              <img
                src={event.image_url}
                alt={event.event_name}
                className="w-full h-auto object-cover max-h-48"
              />
            </div>
          )}

          {/* Event name */}
          <h3 className="text-lg font-bold text-cream mb-2">
            {event.event_name.trim()}
          </h3>

          {/* Presenter */}
          <p className="text-sm text-gray-300 mb-3">
            {event.presenter}
          </p>

          {/* Date */}
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Calendar size={14} />
            <span>{event.event_date}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
